import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import IconButton from 'apollo-react/components/IconButton';
import EllipsisVertical from 'apollo-react-icons/EllipsisVertical';
import FileDocuments from 'apollo-react-icons/FileDocuments';
import Pencil from 'apollo-react-icons/Pencil';
import ChartLine from 'apollo-react-icons/ChartLine';
import styles from './FieldActionsMenu.module.css';

/**
 * FieldActionsMenu - Ellipsis menu with field actions
 *
 * @param {boolean} hasComments - Whether the field has existing comments
 * @param {boolean} hasWaves - Whether the field has wave data
 * @param {boolean} enableAddComment - Whether adding comments is enabled
 * @param {boolean} enableFieldEdit - Whether field editing is enabled
 * @param {boolean} disabled - Whether the form is disabled
 * @param {boolean} enableFieldWaves - Whether field waves viewing is enabled
 * @param {Function} onAddComment - Callback when Add Comment is clicked
 * @param {Function} onEditField - Callback when Edit Field is clicked
 * @param {Function} onViewComments - Callback when comment indicator is clicked
 * @param {Function} onViewFieldWaves - Callback when Field Waves is clicked
 */
const FieldActionsMenu = ({
  hasComments = false,
  hasWaves = false,
  enableAddComment = false,
  enableFieldEdit = false,
  disabled = false,
  enableFieldWaves = false,
  onAddComment,
  onEditField,
  onViewComments,
  onViewFieldWaves,
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  const handleToggle = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    const handleDocumentClick = (event) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleDocumentClick);
    return () => document.removeEventListener('mousedown', handleDocumentClick);
  }, [isOpen]);

  const handleAddComment = useCallback(() => {
    setIsOpen(false);
    if (onAddComment) onAddComment();
  }, [onAddComment]);

  const handleEditField = useCallback(() => {
    setIsOpen(false);
    if (onEditField) onEditField();
  }, [onEditField]);

  const handleViewFieldWaves = useCallback(() => {
    setIsOpen(false);
    if (onViewFieldWaves) onViewFieldWaves();
  }, [onViewFieldWaves]);

  return (
    <div className={styles.container} ref={containerRef}>
      {hasWaves && (
        <button
          type="button"
          className={styles.indicatorButton}
          onClick={onViewFieldWaves}
          title={t('fieldActions.fieldWaves')}
        >
          <ChartLine className={styles.indicatorIcon} />
        </button>
      )}
      {hasComments && (
        <button
          type="button"
          className={styles.indicatorButton}
          onClick={onViewComments}
          title={t('fieldActions.viewAllComments')}
        >
          <FileDocuments className={styles.indicatorIcon} />
        </button>
      )}
      <IconButton
        onClick={handleToggle}
        className={styles.toggleButton}
        size="small"
        title={t('fieldActions.more')}
      >
        <EllipsisVertical className={styles.ellipsisIcon} />
      </IconButton>

      {isOpen && (
        <div className={styles.menu} role="menu">
          {enableAddComment && (
            <button
              type="button"
              className={styles.menuItem}
              onClick={handleAddComment}
            >
              <FileDocuments className={styles.menuItemIcon} />
              <span className={styles.menuItemText}>{t('add_comment')}</span>
            </button>
          )}

          {enableFieldEdit && !disabled && (
            <button
              type="button"
              className={styles.menuItem}
              onClick={handleEditField}
            >
              <Pencil className={styles.menuItemIcon} />
              <span className={styles.menuItemText}>
                {t('fieldActions.editField')}
              </span>
            </button>
          )}

          {enableFieldWaves && (
            <button
              type="button"
              className={styles.menuItem}
              onClick={handleViewFieldWaves}
            >
              <ChartLine className={styles.menuItemIcon} />
              <span className={styles.menuItemText}>
                {t('fieldActions.fieldWaves')}
              </span>
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default FieldActionsMenu;
